import createDebug from 'debug';
import { authFetch } from './client';

const debug = createDebug('app:api:users');
const API_BASE = '/api';

// =============================================================================
// Types
// =============================================================================

export interface User {
	id: number;
	username: string;
	email: string | null;
	display_name: string | null;
	permissions: string[];
	roles?: string[]; // Role names from RBAC system
	is_active: boolean;
	created_at: number;
	updated_at: number;
}

export interface UserDetail extends User {
	auth_methods: AuthMethodSummary[];
	link_count: number;
	queue_count: number;
}

export interface AuthMethodSummary {
	id: number;
	auth_type: 'passkey' | 'password' | 'api_key';
	is_active: boolean;
	created_at: number;
	last_used_at: number | null;
	passkey_device_name?: string;
	api_key_prefix?: string;
	api_key_name?: string;
}

export interface InviteToken {
	id: number;
	token: string;
	user_id: number;
	created_by_user_id: number;
	created_at: number;
	expires_at: number;
	used_at: number | null;
	// Joined fields
	username?: string;
	email?: string;
	created_by_username?: string;
}

export interface CreateUserRequest {
	username: string;
	email?: string;
	display_name?: string;
	permissions?: string[];
}

export interface CreateUserResponse {
	user: User;
	invite: {
		token: string;
		url: string;
		expires_at: number;
	};
}

export interface UpdateUserRequest {
	email?: string;
	display_name?: string;
	permissions?: string[];
	is_active?: boolean;
}

export interface ListUsersResponse {
	users: User[];
	total: number;
	page: number;
	limit: number;
	totalPages: number;
}

export interface RegenerateInviteResponse {
	token: string;
	url: string;
	expires_at: number;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get current authenticated user with full details
 */
export async function getCurrentUser(): Promise<UserDetail> {
	debug('getCurrentUser');

	const response = await authFetch(`${API_BASE}/users/me`);

	if (!response.ok) {
		const data = await response.json();
		throw new Error(data.error || `Failed to get current user: ${response.status}`);
	}

	return response.json();
}

/**
 * List all users with optional filters - ADMIN ONLY (requires users:read permission)
 */
export async function listUsers(params?: {
	page?: number;
	limit?: number;
	is_active?: boolean;
	search?: string;
}): Promise<ListUsersResponse> {
	debug('listUsers: %o', params);

	const query = new URLSearchParams();
	if (params?.page) query.set('page', String(params.page));
	if (params?.limit) query.set('limit', String(params.limit));
	if (params?.is_active !== undefined) query.set('is_active', String(params.is_active));
	if (params?.search) query.set('search', params.search);

	const url = `${API_BASE}/users${query.toString() ? `?${query.toString()}` : ''}`;
	const response = await authFetch(url);

	if (!response.ok) {
		const data = await response.json();
		throw new Error(data.error || `Failed to list users: ${response.status}`);
	}

	return response.json();
}

/**
 * Create a new user with an invite token - ADMIN ONLY (requires users:write permission)
 */
export async function createUser(data: CreateUserRequest): Promise<CreateUserResponse> {
	debug('createUser: %o', data);

	const response = await authFetch(`${API_BASE}/users`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(data),
	});

	if (!response.ok) {
		const errorData = await response.json();
		throw new Error(errorData.error || `Failed to create user: ${response.status}`);
	}

	return response.json();
}

/**
 * Update an existing user - ADMIN ONLY (requires users:write permission)
 */
export async function updateUser(userId: number, data: UpdateUserRequest): Promise<User> {
	debug('updateUser: %d %o', userId, data);

	const response = await authFetch(`${API_BASE}/users/${userId}`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(data),
	});

	if (!response.ok) {
		const errorData = await response.json();
		throw new Error(errorData.error || `Failed to update user: ${response.status}`);
	}

	return response.json();
}

/**
 * Regenerate invite token for a user - ADMIN ONLY (requires users:write permission)
 */
export async function regenerateInvite(userId: number): Promise<RegenerateInviteResponse> {
	debug('regenerateInvite: %d', userId);

	const response = await authFetch(`${API_BASE}/users/${userId}/invite`, {
		method: 'POST',
	});

	if (!response.ok) {
		const errorData = await response.json();
		throw new Error(errorData.error || `Failed to regenerate invite: ${response.status}`);
	}

	return response.json();
}
